import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useDispatch, useSelector } from 'react-redux';

import LocateUserBtn from './LocateUserBtn';
import ToggleHeaderBtn from './ToggleHeaderBtn';
import SearchBeachInput from './SearchBeachInput';

import { setBeachDetails } from '../../redux/actions/beachesActions';
import SwitchFlagQuality from './SwitchFlagQuality';
import I18n from '../../languages/I18n';

const screen_height =
  window.innerHeight ||
  document.documentElement.clientHeight ||
  document.body.clientHeight;

const MODE_LABELS = {
  1: 'map_switch.flag',
  2: 'map_switch.quality',
  3: 'map_switch.ostreopsis',
};

const IMAGE_MARKER_ZOOM_THRESHOLD = 12.5;

const FullScreenMap = (props) => {
  const {
    headerVisible,
    isSmall,
    language,
    showPanel,
    toggleHeaderVisible,
  } = props;
  const dispatch = useDispatch();
  const [map, setMap] = useState(null);
  const mapContainer = useRef(null);
  const isZoomed = useRef(false);

  const mapMarkers = useRef([]);

  const [isLoading, setIsLoading] = useState(false);

  const all_beaches_closed = useSelector(
    (state) => state.beaches.all_beaches_closed,
  );

  const [flagOrQuality, setFlagOrQuality] = useState(all_beaches_closed ? 2 : 1);

  const beaches_list = useSelector((state) => state.beaches.beaches_list);
  const risks = useSelector((state) => state.beaches.risks);

  const [mapHeight, setMapHeight] = useState(
    headerVisible ? screen_height - 110 : screen_height,
  );
  const [offset, setOffset] = useState(
    isSmall ? [0, -(((35 / 100) * mapHeight) / 2 + 50)] : [-250, 0],
  );
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    var new_screen_height =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    setMapHeight(headerVisible ? new_screen_height - 110 : new_screen_height);

    if (map) {
      setTimeout(() => {
        map.resize();
      }, 300);
    }
  }, [headerVisible, map, isSmall]);

  useEffect(() => {
    if (isSmall) {
      setOffset([0, -(((35 / 100) * mapHeight) / 2 + 50)]);
    } else {
      setOffset([-250, 0]);
    }
  }, [isSmall, mapHeight]);

  useEffect(() => {
    mapboxgl.accessToken =
      'pk.eyJ1IjoianVzcHJpZXQiLCJhIjoiY2s5d3N2NDVkMGNhMjNrcXNtZm5veDNidCJ9.Xn0Kn_aD-fm_WS1xjyatgA';
    const initializeMap = ({ setMap, mapContainer }) => {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/juspriet/cjue5mv151kyu1frrx7xq7v7y',
        center: [-1.584926, 43.437091],
        zoom: 11,
      });

      var nav = new mapboxgl.NavigationControl();
      map.addControl(nav, 'bottom-left');
      map.on('load', () => {
        setMap(map);
        map.resize();
      });

      map.on('zoom', function () {
        let newzoomvalue = map.getZoom();
        if (isZoomed.current && newzoomvalue <= IMAGE_MARKER_ZOOM_THRESHOLD) {
          isZoomed.current = false;
          setZoomed(false);
        } else if (!isZoomed.current && newzoomvalue > IMAGE_MARKER_ZOOM_THRESHOLD) {
          isZoomed.current = true;
          setZoomed(true);
        }
      });
    };

    if (!map) {
      initializeMap({ setMap, mapContainer });
    } else {
      map.fitBounds([
        [-1.792123, 43.331476],
        [-1.505278, 43.530038],
      ]);
    }
  }, [map]);

  function getFlagClassName(color, zoomed) {
    const prefix = zoomed ? '' : 'small_';
    const colorMap = {
      6: 'vigie_map',
      5: 'white_map',
      4: 'violet_map',
      3: 'red_map',
      2: 'orange_map',
      1: 'green_map',
      0: 'noflag_map',
    };
    return prefix + (colorMap[color] || 'white_map');
  }

  function getQualityClassName(quality, zoomed) {
    const prefix = zoomed ? '' : 'small_';
    if (quality === 1 || quality === 3) return prefix + 'goodquality_map';
    if (quality === 2 || quality === 4 || quality === 5) return prefix + 'badquality_map';
    return prefix + 'noquality_map';
  }

  function getOstreopsisClassName(ostreopsis, zoomed) {
    const prefix = zoomed ? '' : 'small_';
    if (ostreopsis === 0 || ostreopsis === null || ostreopsis === undefined) return prefix + 'ostreopsis_level0_map';
    if (ostreopsis === 1) return prefix + 'ostreopsis_level1_map';
    if (ostreopsis === 2 || ostreopsis === 3) return prefix + 'ostreopsis_level2_map';
    if (ostreopsis >= 4) return prefix + 'ostreopsis_level4_map';
    return prefix + 'ostreopsis_level0_map';
  }

  const openBeach = useCallback((beach) => {
    if (beach) {
      showPanel();
      dispatch(setBeachDetails(beach));

      if (map) {
        setTimeout(() => {
          map.flyTo({
            center: [parseFloat(beach.lng), parseFloat(beach.lat)],
            offset: offset,
            zoom: 15,
            speed: 0.8,
            curve: 0.6,
          });
        }, 300);
      }
    }
  }, [dispatch, map, offset, showPanel]);

  useEffect(() => {
    if (beaches_list && map) {
      if (mapMarkers.current.length > 0) {
        Array.prototype.forEach.call(mapMarkers.current, function (el) {
          el.remove();
        });
        mapMarkers.current = [];
      }

      beaches_list.forEach((element) => {
        if (flagOrQuality === 1) {
          // Flag mode
          const flagClassName = getFlagClassName(element.color, zoomed);
          const el = document.createElement('div');
          el.className = flagClassName;
          el.addEventListener('click', () => openBeach(element));
          const marker = new mapboxgl.Marker(el)
            .setLngLat([parseFloat(element.lng), parseFloat(element.lat)])
            .addTo(map);
          mapMarkers.current.push(marker);

        } else if (flagOrQuality === 2) {
          // Water quality mode
          const qualityClassName = getQualityClassName(element.quality, zoomed);
          const el = document.createElement('div');
          el.className = qualityClassName;
          el.addEventListener('click', () => openBeach(element));
          const marker = new mapboxgl.Marker(el)
            .setLngLat([parseFloat(element.lng), parseFloat(element.lat)])
            .addTo(map);
          mapMarkers.current.push(marker);

        } else if (flagOrQuality === 3) {
          // Ostreopsis mode
          const ostreopsisClassName = getOstreopsisClassName(element.ostreopsis, zoomed);
          const el = document.createElement('div');
          el.className = ostreopsisClassName;
          el.addEventListener('click', () => openBeach(element));
          const marker = new mapboxgl.Marker(el)
            .setLngLat([parseFloat(element.lng), parseFloat(element.lat)])
            .addTo(map);
          mapMarkers.current.push(marker);
        }
      });
    }

    if (risks && map) {
      if (Number(risks.ostreopsis) === 1) {
        var ostreopsis_map = document.createElement('div');
        ostreopsis_map.className = 'ostreopsis_map';
        var ostreopsis = new mapboxgl.Marker(ostreopsis_map)
          .setLngLat([-1.6753377281135178, 43.46043687911641])
          .addTo(map);
        mapMarkers.current.push(ostreopsis);
      }

      if (Number(risks.meduses) === 1) {
        var meduses_map = document.createElement('div');
        meduses_map.className = 'meduses_map';
        var meduses = new mapboxgl.Marker(meduses_map)
          .setLngLat([-1.7003377281135178, 43.49043687911641])
          .addTo(map);
        mapMarkers.current.push(meduses);
      }

      if (Number(risks.physalies) === 1) {
        var physalies_map = document.createElement('div');
        physalies_map.className = 'physalies_map';
        var physalies = new mapboxgl.Marker(physalies_map)
          .setLngLat([-1.7153377281135178, 43.46043687911641])
          .addTo(map);
        mapMarkers.current.push(physalies);
      }

      if (Number(risks.dangers) === 1) {
        var dangers_map = document.createElement('div');
        dangers_map.className = 'dangers_map';
        var dangers = new mapboxgl.Marker(dangers_map)
          .setLngLat([-1.6613377281135178, 43.48943687911641])
          .addTo(map);
        mapMarkers.current.push(dangers);
      }
    }

  }, [beaches_list, flagOrQuality, map, openBeach, risks, zoomed]);

  const maPosition = (position) => {
    setIsLoading(false);

    if (map) {
      var my_position = document.createElement('div');
      my_position.className = 'my_position';
      new mapboxgl.Marker(my_position)
        .setLngLat([position.coords.longitude, position.coords.latitude])
        .addTo(map);

      setTimeout(() => {
        map.flyTo({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: 15,
          speed: 0.8,
          curve: 0.6,
        });
      }, 300);
    }
  };

  const geolocateUser = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(maPosition);
    }
  };

  return (
    <>
      <div
        ref={(el) => (mapContainer.current = el)}
        style={{
          height: mapHeight,
          width: '100%',
        }}
      />

      <SwitchFlagQuality setFlagOrQuality={setFlagOrQuality} flagOrQuality={flagOrQuality} />

      {/* Floating label capsule showing current mode */}
      <div style={{
        position: 'absolute',
        bottom: 30,
        right: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 10,
        pointerEvents: 'none',
      }}>
        <div style={{
          backgroundColor: '#FFF',
          borderRadius: 20,
          border: '2px solid #F2F2F2',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          paddingVertical: 8,
          paddingHorizontal: 16,
          padding: '6px 16px',
        }}>
          <span style={{
            fontSize: 13,
            color: '#444',
            fontWeight: '500',
            whiteSpace: 'nowrap',
          }}>
            {I18n.t(MODE_LABELS[flagOrQuality])}
          </span>
        </div>
      </div>

      <LocateUserBtn geolocateUser={geolocateUser} isLoading={isLoading} />

      <ToggleHeaderBtn
        headerVisible={headerVisible}
        toggleHeaderVisible={toggleHeaderVisible}
      />

      {headerVisible && (
        <SearchBeachInput
          isSmall={isSmall}
          openBeach={openBeach}
          beaches_list={beaches_list}
          language={language}
        />
      )}
    </>
  );
};

export default FullScreenMap;
